import { prepareAIMedia, validateAIMediaUrl } from './ai-media'

const validDeliveryId = (id: unknown): id is string => typeof id === 'string' && id.length > 0 && id.length <= 256 && !/[\s\u0000-\u001f\u007f]/.test(id)
const prefix = 'dmex:telegram-ai:v1:'
const failure = (code: string, status = 'failed') => ({ ok: false, status, code })
// Claims only need to outlive the send and its deadline; receipts only need to answer retries.
const claimRetentionMs = 10 * 60000
const receiptRetentionMs = 86400000
const maxRetained = 2000

/** PC owns authorization; this page additionally fences repeated platform sends. */
export function createTelegramAIActionHandler(options: {
  getManager: () => any; getSelfId: () => string | number;
  storage?: Storage; crypto?: Crypto; prepareMedia?: typeof prepareAIMedia; timeoutMs?: number
}) {
  const inflight = new Map<string, { fingerprint: string; task: Promise<any> }>()
  const storage = options.storage ?? localStorage, crypt = options.crypto ?? crypto
  const hash = async (value: string) => Array.from(new Uint8Array(await crypt.subtle.digest('SHA-256', new TextEncoder().encode(value))), b => b.toString(16).padStart(2, '0')).join('')
  const read = (key: string) => { try { return storage.getItem(key) } catch { return null } }
  /** Ledger writes are best effort: a full local ledger must never block a delivery. */
  const remember = (key: string, value: any) => {
    try { storage.setItem(key, JSON.stringify(value)) } catch { /* In-memory inflight still dedupes. */ }
  }
  const forget = (key: string) => { try { storage.removeItem(key) } catch { /* ignore */ } }
  /** Drop expired entries, then the oldest claims once the ledger is full. */
  const prune = (now: number) => {
    const keys: string[] = []
    try {
      for (let index = storage.length - 1; index >= 0; index--) {
        const key = storage.key(index)
        if (key?.startsWith(prefix)) keys.push(key)
      }
    } catch { return }
    const live: Array<{ key: string; retainUntil: number }> = []
    for (const key of keys) {
      let entry: any = null
      try { entry = JSON.parse(read(key) ?? 'null') } catch { entry = null }
      if (!entry || !Number.isFinite(entry.retainUntil) || entry.retainUntil <= now) { forget(key); continue }
      live.push({ key, retainUntil: entry.retainUntil })
    }
    if (live.length < maxRetained) return
    live.sort((a, b) => a.retainUntil - b.retainUntil)
    for (const { key } of live.slice(0, live.length - maxRetained + 1)) forget(key)
  }
  const wait = async (task: Promise<any>) => {
    let timer: ReturnType<typeof setTimeout>
    try { return await Promise.race([task, new Promise(resolve => { timer = setTimeout(() => resolve(failure('SEND_TIMEOUT', 'unknown')), options.timeoutMs ?? 330000) })]) }
    finally { clearTimeout(timer) }
  }
  return async (data: any): Promise<any> => {
    if (!['aireply', 'aisend'].includes(data?.action)) return false
    const inputPayload = data.payload
    const p = { ...inputPayload, userId: typeof inputPayload?.userId === 'number' && Number.isSafeInteger(inputPayload.userId) && inputPayload.userId > 0 ? String(inputPayload.userId) : inputPayload?.userId }, kind = p.msgType ?? 'text'
    if (typeof p?.userId !== 'string' || !p.userId.trim() || typeof p?.msg !== 'string' || !p.msg.trim() ||
        !['text', 'image', 'video', 'voice'].includes(kind) ||
        (kind === 'text' && p.msg.length > 10000) ||
        (p.caption != null && (typeof p.caption !== 'string' || p.caption.length > 1024))) return failure('CONTENT_INVALID')
    try { if (kind !== 'text') validateAIMediaUrl(p.msg) } catch { return failure('MEDIA_URL_INVALID') }
    const manager = options.getManager(), self = String(options.getSelfId())
    if (!/^[1-9][0-9]*$/.test(self) || !manager) return failure('ACCOUNT_NOT_READY')
    if (p.expectedSelfId !== undefined && p.expectedSelfId !== self) return failure('ACCOUNT_MISMATCH')
    // Old clients send text without a delivery ID; preserve their boolean completion contract.
    if (p.deliveryId === undefined) {
      if (kind !== 'text') return failure('DELIVERY_ID_REQUIRED')
      try { return await wait(Promise.resolve(manager.sendText(p.userId, p.msg))) === undefined }
      catch { return false }
    }
    // Structural only: a backlogged message may already be past its deadline and is still business.
    const expires = Date.parse(p.expiresAt), valid = () => Number.isFinite(expires) && expires - Date.now() <= 86400000 && String(options.getSelfId()) === self
    if (!validDeliveryId(p.deliveryId) || !valid()) return failure('DELIVERY_INVALID')
    // The deadline is not part of the identity: a retry may refresh it.
    const input = JSON.stringify([data.action, p.userId, kind, p.msg, p.caption ?? ''])
    const key = prefix + self + ':' + p.deliveryId
    const pending = inflight.get(key)
    if (pending) return pending.fingerprint === input ? pending.task : failure('DELIVERY_CONFLICT')
    const task = (async () => {
      let prepared: Awaited<ReturnType<typeof prepareAIMedia>> | undefined
      const receipt = (value: any) => ({ ...value, deliveryId: p.deliveryId, userId: p.userId, msgType: kind })
      try {
        const fingerprint = await hash(input)
        if (!valid()) return failure('DELIVERY_INVALID')
        const raw = read(key)
        if (raw !== null) {
          let saved: any = null
          try { saved = JSON.parse(raw) } catch { saved = null }
          // Expiry changes never invalidate an existing claim or receipt.
          if (saved) {
            if (saved.fingerprint !== fingerprint) return failure('DELIVERY_CONFLICT')
            return saved.state === 'sent' ? receipt({ ok: true, status: 'sent', platformMessageIds: saved.platformMessageIds }) : failure('DELIVERY_UNCONFIRMED', 'unknown')
          }
        }
        const now = Date.now()
        prune(now)
        remember(key, { fingerprint, expires, retainUntil: Math.max(expires, now) + claimRetentionMs, state: 'claimed' })
        if (kind !== 'text') prepared = await (options.prepareMedia ?? prepareAIMedia)(p.msg, kind)
        // Only fence a retry once a send was actually attempted.
        if (!valid()) { forget(key); return failure('DELIVERY_INVALID') }
        let result: any
        if (kind === 'text') {
          const completed = await wait(Promise.resolve(manager.sendText(p.userId, p.msg)))
          result = completed === undefined ? { ok: true, status: 'sent', platformMessageIds: [] } : failure('SEND_UNCONFIRMED', 'unknown')
        } else if (typeof manager.sendConfirmedAIMessage === 'function') {
          const { dispose, ...media } = prepared ?? {} as any
          result = await wait(Promise.resolve(manager.sendConfirmedAIMessage(p.userId, { msgType: kind, msg: kind === 'text' ? p.msg : undefined, caption: p.caption ?? '', ...media })))
        } else return failure('MEDIA_NOT_SUPPORTED')
        if (result?.ok !== true || result.status !== 'sent') return receipt(failure(result?.code || 'SEND_UNCONFIRMED', result?.status || 'unknown'))
        const ids = Array.isArray(result.platformMessageIds) ? result.platformMessageIds.filter((id: any) => typeof id === 'string' && /^\d+$/.test(id)).slice(0, 100) : []
        remember(key, { fingerprint, expires, retainUntil: Date.now() + receiptRetentionMs, state: 'sent', platformMessageIds: ids })
        return receipt({ ok: true, status: 'sent', platformMessageIds: ids })
      } catch (e: any) {
        const known = ['MEDIA_URL_INVALID', 'MEDIA_DOWNLOAD_TIMEOUT', 'MEDIA_DOWNLOAD_FAILED', 'MEDIA_TYPE_INVALID', 'MEDIA_TOO_LARGE', 'MEDIA_EMPTY',
          'VOICE_OPUS_REQUIRED', 'MEDIA_METADATA_TIMEOUT', 'MEDIA_DECODE_FAILED', 'MEDIA_METADATA_INVALID']
        return receipt(known.includes(e?.code) ? failure(e.code) : failure('SEND_UNCONFIRMED', 'unknown'))
      }
      finally { prepared?.dispose() }
    })().finally(() => { inflight.delete(key) })
    inflight.set(key, { fingerprint: input, task })
    return task
  }
}
